import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import {
  ANA_PARA_BIRIMI, PARA_BIRIMLERI, duzelt, para, paraBirimiNormalle, paraBirimiSimgesi,
} from '@nero/shared';
import type { ParaBirimi, YaziDuzeniTuru } from '@nero/shared';
import { Simge } from './Simge.js';
import { kurSatiri, tlKarsiligi, useKur } from '../lib/kur.js';

/* ---------- Alan sarmalayıcı ---------- */

export function Alan({
  etiket, ipucu, hata, zorunlu, children,
}: { etiket?: ReactNode; ipucu?: ReactNode; hata?: string | null; zorunlu?: boolean; children: ReactNode }) {
  return (
    <div className="alan">
      {etiket && (
        <label>
          {etiket}
          {zorunlu && <span style={{ color: 'var(--kirmizi)' }} aria-hidden> *</span>}
        </label>
      )}
      {children}
      {hata ? <div className="hata">{hata}</div> : ipucu ? <div className="ipucu">{ipucu}</div> : null}
    </div>
  );
}

/* ---------- Metin girdisi ---------- */

interface GirdiOzellikleri extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  etiket?: ReactNode;
  ipucu?: ReactNode;
  hata?: string | null;
  onChange?: (deger: string) => void;
  /**
   * Yazım düzeni kuralı. Verilirse ALANDAN ÇIKINCA değer düzeltilir:
   * "ahmet yilmaz" → "Ahmet Yılmaz", "27abc123" → "27 ABC 123".
   * Yazarken hiçbir şeye karışılmaz, imleç kaymaz. Sunucu da kaydederken
   * aynı kuralı uygular; bu yüzden burada düzelmese bile kayıt düzgün olur.
   */
  duzen?: YaziDuzeniTuru;
}

export function Girdi({
  etiket, ipucu, hata, onChange, duzen, onBlur, className = '', required, ...kalan
}: GirdiOzellikleri) {
  function alandanCik(e: FocusEvent<HTMLInputElement>) {
    if (duzen && onChange) {
      const duzgun = duzelt(duzen, e.target.value);
      if (duzgun !== e.target.value) onChange(duzgun);
    }
    onBlur?.(e);
  }

  return (
    <Alan etiket={etiket} ipucu={ipucu} hata={hata} zorunlu={required}>
      <input
        className={`girdi ${className}`}
        aria-invalid={hata ? 'true' : undefined}
        onChange={onChange ? (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value) : undefined}
        onBlur={duzen || onBlur ? alandanCik : undefined}
        required={required}
        {...kalan}
      />
    </Alan>
  );
}

/* ---------- Para girdisi ---------- */

/**
 * PARA GİRDİSİ — isteğe bağlı para birimi seçicisiyle.
 *
 * ─── İSTEK ───
 * "güncel kur yazmaya gerek yok. sadece mesela 400 euro ödeme aldıysam
 *  euro seçip girebileyim."
 *
 * Bu yüzden burada KUR ALANI YOKTUR. Kullanıcı ₺ / $ / € düğmelerinden
 * birine basar ve sayıyı yazar; o kadar. TL dışında bir birim seçiliyse
 * girdinin ALTINDA bilgi satırı belirir:
 *   "≈ 20.000,00 ₺ · 1 € = 50,0000 · 14.09.2026"
 * Bu satır bir alan değildir, doldurulmaz; yalnız ne kaydedileceğini
 * gösterir. Kullanıcı parasını körlemesine girmez.
 *
 * `paraBirimi` verilmezse bileşen ESKİSİ GİBİ davranır (yalnız ₺). Böylece
 * dövize açılmayan alanlar (ör. ödenen tutarı) hiç değişmez.
 */
export function ParaGirdi({
  etiket, ipucu, hata, deger, onChange, paraBirimi, onParaBirimi, ...kalan
}: {
  etiket?: ReactNode; ipucu?: ReactNode; hata?: string | null;
  deger: number | string; onChange: (deger: string) => void;
  /** Verilirse para birimi seçici görünür. */
  paraBirimi?: ParaBirimi;
  onParaBirimi?: (birim: ParaBirimi) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  const kayit = useKur();
  const birim = paraBirimiNormalle(paraBirimi);
  const secilebilir = !!paraBirimi && !!onParaBirimi;
  const tl = secilebilir ? tlKarsiligi(deger, birim, kayit) : null;
  const satir = secilebilir ? kurSatiri(birim, kayit) : null;

  /* İpucu satırı: TL'de yalnız simge, dövizde TL karşılığı ve kur. */
  const altSatir: ReactNode = secilebilir && birim !== ANA_PARA_BIRIMI
    ? (
      <span className="kur-ipucu">
        {tl !== null && <strong>≈ {para(tl)}</strong>}
        {satir && <span className="kur-ipucu-kaynak">{tl !== null ? ' · ' : ''}{satir}</span>}
        {kayit?.bayat && <span className="kur-bayat" title="Kur güncellenemedi; en son bilinen kur kullanılıyor"> · kur eski</span>}
      </span>
    )
    : (ipucu ?? paraBirimiSimgesi(birim));

  return (
    <Alan etiket={etiket} ipucu={altSatir} hata={hata}>
      <div className={secilebilir ? 'para-satir' : undefined}>
        <input
          className="girdi girdi-para"
          type="number" inputMode="decimal" step="0.01" min="0"
          value={deger}
          onChange={(e) => onChange(e.target.value)}
          {...kalan}
        />
        {secilebilir && (
          <div className="para-birimi-secici" role="group" aria-label="Para birimi">
            {PARA_BIRIMLERI.map((p) => (
              <button
                key={p.kod}
                type="button"
                className={`para-birimi-dugme${p.kod === birim ? ' secili' : ''}`}
                aria-pressed={p.kod === birim}
                title={p.ad}
                onClick={() => onParaBirimi?.(p.kod)}
              >
                {p.simge}
              </button>
            ))}
          </div>
        )}
      </div>
    </Alan>
  );
}

/* ---------- Çok satırlı metin ---------- */

export function MetinAlani({ etiket, ipucu, hata, onChange, duzen, onBlur, ...kalan }: {
  etiket?: ReactNode; ipucu?: ReactNode; hata?: string | null;
  onChange?: (deger: string) => void;
  duzen?: YaziDuzeniTuru;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'>) {
  function alandanCik(e: FocusEvent<HTMLTextAreaElement>) {
    if (duzen && onChange) {
      const duzgun = duzelt(duzen, e.target.value);
      if (duzgun !== e.target.value) onChange(duzgun);
    }
    onBlur?.(e);
  }
  return (
    <Alan etiket={etiket} ipucu={ipucu} hata={hata}>
      <textarea
        className="metin-alani"
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onBlur={duzen || onBlur ? alandanCik : undefined}
        {...kalan}
      />
    </Alan>
  );
}

/* ---------- Seçim ---------- */

export function Secim({ etiket, ipucu, hata, secenekler, onChange, bosMetin, ...kalan }: {
  etiket?: ReactNode; ipucu?: ReactNode; hata?: string | null;
  secenekler: Array<{ deger: string; ad: string }>;
  bosMetin?: string;
  onChange?: (d: string) => void;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'>) {
  return (
    <Alan etiket={etiket} ipucu={ipucu} hata={hata}>
      <select className="secim" onChange={onChange ? (e) => onChange(e.target.value) : undefined} {...kalan}>
        {bosMetin && <option value="">{bosMetin}</option>}
        {secenekler.map((s) => <option key={s.deger} value={s.deger}>{s.ad}</option>)}
      </select>
    </Alan>
  );
}

/* ---------- Arama kutusu ---------- */

export function Arama({ deger, onChange, yerTutucu = 'Ara…', ...kalan }: {
  deger: string; onChange: (d: string) => void; yerTutucu?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>) {
  return (
    <div className="arama esnek">
      <Simge ad="ara" className="simge" />
      <input
        className="girdi" type="search" value={deger} placeholder={yerTutucu}
        onChange={(e) => onChange(e.target.value)} {...kalan}
      />
    </div>
  );
}

/* ---------- Onay kutusu ---------- */

export function Onay({ etiket, isaretli, onChange, ...kalan }: {
  etiket: ReactNode; isaretli: boolean; onChange: (d: boolean) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked' | 'type'>) {
  return (
    <label className="onay">
      <input type="checkbox" checked={isaretli} onChange={(e) => onChange(e.target.checked)} {...kalan} />
      <span>{etiket}</span>
    </label>
  );
}

/* ---------- Seçenek şeridi ---------- */

export function SecenekSerit<T extends string>({ deger, secenekler, onChange }: {
  deger: T;
  secenekler: Array<{ deger: T; ad: string }>;
  onChange: (d: T) => void;
}) {
  return (
    <div className="secenek-serit" role="tablist">
      {secenekler.map((s) => (
        <button
          key={s.deger} role="tab" aria-selected={s.deger === deger}
          className={s.deger === deger ? 'etkin' : ''}
          onClick={() => onChange(s.deger)}
        >
          {s.ad}
        </button>
      ))}
    </div>
  );
}
